import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, WarningCircle, CircleNotch, Eye, Copy, ArrowRight, X } from '@phosphor-icons/react';

const HerokuNewDeployment = () => {
  const [templates, setTemplates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ template_id: '', account_id: '', count: 1 });
  const [deployments, setDeployments] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [tplRes, accRes] = await Promise.all([
        axios.get(`/api/heroku/templates`, { withCredentials: true }),
        axios.get(`/api/heroku/accounts`, { withCredentials: true })
      ]);
      setTemplates(tplRes.data);
      setAccounts(accRes.data);
      
      if (tplRes.data.length > 0) setForm(f => ({...f, template_id: tplRes.data[0].id}));
      if (accRes.data.length > 0) setForm(f => ({...f, account_id: accRes.data[0].id}));
    } catch (e) {
      toast.error("Failed to load templates or accounts");
    }
  };

  const handleDeploy = async () => {
    if (!form.template_id || !form.account_id) return toast.error("Select a template and an account.");
    
    setDeploying(true);
    setShowCompletionModal(false);
    try {
      const res = await axios.post(`/api/heroku/deployments`, form, { withCredentials: true });
      const initialDeps = res.data.map(d => ({ ...d, url: null, error: null, app_name: '' }));
      setDeployments(initialDeps);

      initialDeps.forEach((dep, index) => {
        const poll = async () => {
          try {
            const pollRes = await axios.get(`/api/heroku/deployments/${dep.id}`, { withCredentials: true });
            setDeployments(prev => {
              const arr = [...prev];
              if (pollRes.data.status === 'live') {
                arr[index] = { ...arr[index], status: 'live', url: pollRes.data.public_url, app_name: pollRes.data.app_name };
              } else if (pollRes.data.status === 'failed') {
                arr[index] = { ...arr[index], status: 'failed', error: pollRes.data.error_msg || "Failed", app_name: pollRes.data.app_name };
              } else {
                arr[index] = { ...arr[index], status: pollRes.data.status, app_name: pollRes.data.app_name };
              }
              return arr;
            });

            if (pollRes.data.status !== 'live' && pollRes.data.status !== 'failed') {
              setTimeout(poll, 4000);
            }
          } catch (err) {
            setDeployments(prev => {
              const arr = [...prev];
              arr[index] = { ...arr[index], status: 'failed', error: "Polling error" };
              return arr;
            });
          }
        };
        poll();
      });
    } catch (err) {
      toast.error("Failed to trigger deployments");
      setDeploying(false);
    }
  };

  // Show completion modal once every deployment in the batch finishes
  useEffect(() => {
    if (deploying && deployments.length > 0) {
      const allDone = deployments.every(d => d.status === 'live' || d.status === 'failed');
      if (allDone) {
        setDeploying(false);
        setShowCompletionModal(true);
      }
    }
  }, [deployments, deploying]);

  const handleCopyAll = () => {
    const urls = deployments.filter(d => d.status === 'live' && d.url).map(d => d.url).join('\n');
    if (urls) {
      navigator.clipboard.writeText(urls);
      toast.success("All Live URLs copied to clipboard!");
    } else {
      toast.error("No Live URLs to copy.");
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'initializing': return <span className="text-yellow-400 text-xs font-bold animate-pulse">Initializing...</span>;
      case 'building': return <span className="text-[#8b60ed] text-xs font-bold animate-pulse flex items-center gap-1"><CircleNotch className="animate-spin" /> Building...</span>;
      case 'live': return <span className="text-[#39FF14] text-xs font-bold flex items-center gap-1"><CheckCircle /> Live</span>;
      case 'failed': return <span className="text-red-500 text-xs font-bold flex items-center gap-1"><WarningCircle /> Failed</span>;
      default: return null;
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <h1 className="text-4xl font-black text-[#8b60ed]">NEW HEROKU DEPLOYMENT</h1>
      
      <div className="bg-card border border-white/10 p-8 max-w-xl space-y-6">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Select Template</label>
          <select 
            value={form.template_id} 
            onChange={(e) => setForm({...form, template_id: e.target.value})}
            className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#8b60ed] appearance-none rounded-none"
          >
            <option value="">-- Choose a git template --</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Select Heroku Account</label>
          <select 
            value={form.account_id} 
            onChange={(e) => setForm({...form, account_id: e.target.value})}
            className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#8b60ed] appearance-none rounded-none"
          >
            <option value="">-- Choose an account --</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Number of Copies</label>
          <input 
            type="number" min="1" max="20"
            value={form.count}
            onChange={(e) => setForm({...form, count: parseInt(e.target.value) || 1})}
            className="w-full bg-[#0A0B0E] border border-white/10 p-3 font-mono text-xl focus:outline-none focus:border-[#8b60ed]"
          />
        </div>

        <button 
          onClick={handleDeploy}
          disabled={deploying || !form.template_id || !form.account_id}
          className="w-full bg-[#8b60ed] text-white px-8 py-4 font-black uppercase tracking-wider hover:bg-[#724dd4] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          DEPLOY TO HEROKU <ArrowRight size={20} />
        </button>
      </div>

      {deployments.length > 0 && (
        <div className="bg-card border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 bg-black/20 flex justify-between items-center">
            <h3 className="font-bold uppercase tracking-wider">Deployment Results</h3>
            {!deploying && (
              <button 
                data-testid="heroku-copy-all-btn"
                onClick={handleCopyAll}
                className="bg-[#0A0B0E] border border-white/20 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors flex items-center gap-2"
              >
                <Copy size={16} /> Copy All
              </button>
            )}
          </div>
          <table className="w-full text-left text-sm border-collapse">
            <tbody>
              {deployments.map((dep, i) => (
                <tr key={i} className="border-b border-white/10 hover:bg-white/5">
                  <td className="p-4 font-mono font-bold w-1/4">{dep.app_name || <span className="text-slate-500 font-normal">Pending...</span>}</td>
                  <td className="p-4 w-1/6">{getStatusDisplay(dep.status)}</td>
                  <td className="p-4 font-mono">
                    {dep.status === 'live' && dep.url && (
                      <div className="flex items-center gap-2">
                        <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 font-mono truncate w-48 select-all" title={dep.url}>
                          {dep.url}
                        </div>
                        <button onClick={() => { navigator.clipboard.writeText(dep.url); toast.success("Copied!"); }} className="text-slate-400 hover:text-white transition-colors p-1"><Copy size={16} /></button>
                        <a href={dep.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#8b60ed] transition-colors p-1"><Eye size={16} /></a>
                      </div>
                    )}
                    {dep.status === 'failed' && <span className="text-red-500 text-xs">{dep.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="heroku-completion-modal">
          <div className="bg-card border border-[#8b60ed]/50 p-8 w-full max-w-2xl relative animate-in zoom-in-95 duration-200 shadow-[0_0_20px_rgba(139,96,237,0.2)]">
            <button onClick={() => setShowCompletionModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="heroku-modal-close-btn">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-2 text-[#8b60ed]">DEPLOYMENT BATCH COMPLETE</h2>
            <p className="text-slate-400 mb-6">Your Heroku apps have finished deploying.</p>
            
            <div className="bg-[#0A0B0E] border border-white/10 p-4 max-h-[300px] overflow-y-auto mb-6 font-mono text-sm space-y-1">
              {deployments.map((dep, i) => (
                <div key={i} className="flex items-center gap-2">
                  {dep.status === 'live' ? (
                    <span className="text-[#39FF14]">{dep.url}</span>
                  ) : (
                    <span className="text-red-500 line-through">{dep.app_name || 'App'} (Failed)</span>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <button 
                data-testid="heroku-modal-copy-btn"
                onClick={handleCopyAll}
                className="flex-1 bg-[#8b60ed] text-white py-3 font-bold uppercase tracking-wider hover:bg-[#724dd4] transition-colors flex items-center justify-center gap-2"
              >
                <Copy size={20} /> Copy Live URLs
              </button>
              <button 
                onClick={() => setShowCompletionModal(false)}
                className="flex-1 bg-transparent border border-white/20 text-white py-3 font-bold uppercase tracking-wider hover:bg-white/10 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerokuNewDeployment;